import cn from 'classnames';
import { CSSProperties, FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { RecordingStopReason } from '@/shared/api/transcriptor';
import {
  useAppDispatch,
  useAppSelector,
  useUserTimezone,
} from '@/shared/hooks';
import { debug, trackEvent } from '@/shared/lib';
import { formatLocalizedDateTime } from '@/shared/lib/helpers/formatLocalizedDateTime';
import { formatTime } from '@/shared/lib/helpers/formatTime';
import { Button } from '@/shared/ui';

import { transcriptorModel } from '@/entities/transcriptor';

import { audioRecordingService } from '@/features/audioRecording';

import {
  audioIssueMessages,
  DICTATION_COUNTDOWN_ENDING_SECONDS,
  VISUALIZER_BAR_COUNT,
  VISUALIZER_BAR_MIN_HEIGHT,
  VISUALIZER_FRAME_INTERVAL_MS,
} from '../../config';
import { useDictationCountdown, useRecordingStop } from '../../hooks';
import { computeVisualizerBars } from '../../lib';

import styles from './RecordingControls.module.scss';

const { pauseRecording, resumeRecording, startUploading, stopRecording } =
  transcriptorModel.actions;

const {
  selectAudioIssue,
  selectConsultationId,
  selectFragmentId,
  selectIsInterrupted,
  selectIsOffline,
  selectIsPaused,
  selectIsProcessing,
  selectIsRecording,
  selectIsSignalLost,
  selectIsUploading,
  selectLastSavedAt,
  selectRecordingTime,
  selectRecordingMode,
} = transcriptorModel.selectors;

const { finishInterruptedRecording } = transcriptorModel.thunks;

type Props = {
  className?: string;
};

// The recording's state in one line: listening, paused or processing.
export const RecordingStatus: FC<Props> = ({ className }) => {
  const isRecording = useAppSelector(selectIsRecording);
  const isPaused = useAppSelector(selectIsPaused);
  const isUploading = useAppSelector(selectIsUploading);
  const isProcessing = useAppSelector(selectIsProcessing);
  const isInterrupted = useAppSelector(selectIsInterrupted);

  const statusText =
    isProcessing || isUploading ? (
      <FormattedMessage
        id="recordingToast.statusProcessing"
        defaultMessage="Processing…"
      />
    ) : isPaused ? (
      <FormattedMessage id="recordingToast.paused" defaultMessage="Paused" />
    ) : isRecording ? (
      <FormattedMessage
        id="recordingToast.statusListening"
        defaultMessage="Listening…"
      />
    ) : null;

  return statusText && !isInterrupted ? (
    <p
      className={cn('p4', styles.status, className)}
      data-component="recording-toast-status"
    >
      {statusText}
    </p>
  ) : null;
};

// The recording's controls, the same in the corner window and on the
// recording's page: its warnings, the time (and a dictation's time left), the
// level, pause and Finish.
export const RecordingControls: FC<Props> = ({ className }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const userTimezone = useUserTimezone();
  const isRecording = useAppSelector(selectIsRecording);
  const isPaused = useAppSelector(selectIsPaused);
  const isUploading = useAppSelector(selectIsUploading);
  const isProcessing = useAppSelector(selectIsProcessing);
  const isInterrupted = useAppSelector(selectIsInterrupted);
  const isSignalLost = useAppSelector(selectIsSignalLost);
  const isOffline = useAppSelector(selectIsOffline);
  const audioIssue = useAppSelector(selectAudioIssue);
  const recordingTime = useAppSelector(selectRecordingTime);
  const lastSavedAt = useAppSelector(selectLastSavedAt);
  const consultationId = useAppSelector(selectConsultationId);
  const fragmentId = useAppSelector(selectFragmentId);
  const dictation = useAppSelector(selectRecordingMode) === 'dictation';
  const [visualizerHeights, setVisualizerHeights] = useState<number[]>(
    Array(VISUALIZER_BAR_COUNT).fill(VISUALIZER_BAR_MIN_HEIGHT),
  );

  useEffect(() => {
    if (!isRecording || isPaused) {
      setVisualizerHeights(
        Array(VISUALIZER_BAR_COUNT).fill(VISUALIZER_BAR_MIN_HEIGHT),
      );
      return;
    }

    let animationFrameId: number;
    let lastUpdateTime = 0;

    const updateVisualizer = (timestamp: number) => {
      if (timestamp - lastUpdateTime >= VISUALIZER_FRAME_INTERVAL_MS) {
        setVisualizerHeights(
          computeVisualizerBars(
            audioRecordingService.getAudioLevel(),
            timestamp,
          ),
        );
        lastUpdateTime = timestamp;
      }

      animationFrameId = requestAnimationFrame(updateVisualizer);
    };

    animationFrameId = requestAnimationFrame(updateVisualizer);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isRecording, isPaused]);

  const remainingSeconds = useDictationCountdown();
  const stop = useRecordingStop();

  const finishRecording = async (stopReason: RecordingStopReason) => {
    try {
      dispatch(startUploading());
      await audioRecordingService.stopRecording(stopReason);
      // stopRecording action is dispatched by the onStopped callback in useStartRecording
    } catch (error) {
      debug(error);
    }
  };

  const finishInterrupted = async () => {
    try {
      if (consultationId && fragmentId) {
        dispatch(stopRecording());
        if (!audioRecordingService.finishInterruptedRecording()) {
          await dispatch(
            finishInterruptedRecording({
              consultationId,
              fragmentId,
              reason: 'client_interrupted',
            }),
          ).unwrap();
        }
      }
    } catch (error) {
      debug(error);
    }
  };

  const formattedLastSavedTime = lastSavedAt
    ? formatLocalizedDateTime({
        date: lastSavedAt,
        intl,
        timeZone: userTimezone,
      }).formattedTime
    : null;

  return (
    <div className={className}>
      {isOffline && isRecording && !isInterrupted && (
        <div className={styles.offlineBanner}>
          <FormattedMessage
            id="recordingToast.reconnecting"
            defaultMessage="Reconnecting... Your recording is saved and secure."
          />
        </div>
      )}

      {audioIssue &&
        isRecording &&
        !isPaused &&
        !isInterrupted &&
        !isSignalLost && (
          <div className={styles.audioIssueBanner}>
            <FormattedMessage {...audioIssueMessages[audioIssue]} />
          </div>
        )}

      {isSignalLost && isRecording && !isPaused && !isInterrupted && (
        <div className={styles.interruptedSection}>
          <div className={styles.interruptedMessage}>
            {audioIssue && audioIssue !== 'silence' ? (
              <FormattedMessage {...audioIssueMessages[audioIssue]} />
            ) : (
              <FormattedMessage
                id="recordingToast.noAudioSignal"
                defaultMessage="No audio for over a minute. Check your microphone or generate the transcript from what was recorded."
              />
            )}
          </div>
          <div className={styles.controls}>
            <Button
              variant="primary"
              size="small"
              onClick={() => finishRecording('client_interrupted')}
              className={styles.controlButton}
              disabled={isUploading}
            >
              <FormattedMessage
                id="recordingToast.generateTranscript"
                defaultMessage="Generate Transcript"
              />
            </Button>
          </div>
        </div>
      )}

      {isInterrupted && !isProcessing && (
        <div className={styles.interruptedSection}>
          <div className={styles.interruptedMessage}>
            <FormattedMessage
              id="recordingToast.recordingStopped"
              defaultMessage="Recording stopped - last saved at {time}. Device may have disconnected."
              values={{ time: formattedLastSavedTime || '--:--' }}
            />
          </div>
          <div className={styles.controls}>
            {dictation ? (
              <Button
                onClick={() =>
                  dispatch(transcriptorModel.actions.resetRecording())
                }
              >
                <FormattedMessage id="global.close" defaultMessage="Close" />
              </Button>
            ) : (
              <Button
                variant="primary"
                size="small"
                onClick={() => void finishInterrupted()}
                className={styles.controlButton}
              >
                <FormattedMessage
                  id="recordingToast.generateTranscript"
                  defaultMessage="Generate Transcript"
                />
              </Button>
            )}
          </div>
        </div>
      )}

      {!isProcessing && !isInterrupted && (
        <div className={styles.recordingStatus}>
          <div
            className={cn(styles.recordingIndicator, {
              [styles.recording]: isRecording && !isPaused,
            })}
          />
          <span className={styles.time} role="timer">
            {formatTime(recordingTime)}
          </span>
          {remainingSeconds !== null && (
            <span
              className={cn(
                'p4',
                styles.countdown,
                remainingSeconds <= DICTATION_COUNTDOWN_ENDING_SECONDS &&
                  styles.countdownEnding,
              )}
              data-component="recording-dictation-countdown"
            >
              <FormattedMessage
                id="recordingToast.dictationRemaining"
                defaultMessage="{time} left"
                values={{ time: formatTime(remainingSeconds) }}
              />
            </span>
          )}
          {isPaused && (
            <span className={styles.pausedText}>
              <FormattedMessage
                id="recordingToast.paused"
                defaultMessage="Paused"
              />
            </span>
          )}
          <div className={styles.visualizer}>
            {visualizerHeights.map((height, index) => (
              <div
                key={index}
                className={cn(styles.visualizerBar, {
                  [styles.paused]: isPaused,
                })}
                style={{ '--bar-height': `${height}px` } as CSSProperties}
              />
            ))}
          </div>

          <div className={styles.controls}>
            {isRecording && !isPaused && (
              <Button
                variant="secondary"
                size="medium"
                icon="pause"
                aria-label={intl.formatMessage({
                  id: 'recordingToast.pause',
                  defaultMessage: 'Pause',
                })}
                onClick={() => {
                  trackEvent({
                    eventName:
                      'PatientProfilePageTranscriptorRecordModalPauseButtonClicked',
                  });

                  audioRecordingService.pauseRecording();
                  dispatch(pauseRecording());
                }}
                className={styles.controlButton}
                disabled={isUploading}
              />
            )}
            {isRecording && isPaused && (
              <Button
                variant="primary"
                size="medium"
                icon="play"
                aria-label={intl.formatMessage({
                  id: 'recordingToast.resume',
                  defaultMessage: 'Resume',
                })}
                onClick={() => {
                  trackEvent({
                    eventName:
                      'PatientProfilePageTranscriptorRecordModalResumeButtonClicked',
                  });

                  audioRecordingService.resumeRecording();
                  dispatch(resumeRecording());
                }}
                className={styles.controlButton}
                disabled={isUploading}
              />
            )}
            <Button
              variant="primary"
              size="medium"
              danger
              icon="stop"
              loading={isUploading}
              aria-label={intl.formatMessage({
                id: 'recordingToast.stop',
                defaultMessage: 'Finish',
              })}
              onClick={() => {
                trackEvent({
                  eventName:
                    'PatientProfilePageTranscriptorRecordModalFinishButtonClicked',
                });
                void stop();
              }}
              className={styles.controlButton}
              disabled={isUploading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// The controls in a panel of their own, as the recording's page shows them.
export const RecordingControlsPanel: FC<Props> = ({ className }) => (
  <div
    className={cn(styles.panel, className)}
    data-component="recording-toast-controls"
  >
    <RecordingStatus />
    <RecordingControls />
  </div>
);
